import { useEffect, useMemo, useRef, useState } from 'react';
import { Shield, Clock, Zap, Target, HelpCircle, FastForward, Coins, Flame } from 'lucide-react';
import { useQuiz } from '../context/QuizContext';
import { getAjudasMaxPorFase, shuffleArray } from '../utils/quizUtils';
import { useGameAudio } from '../hooks/useGameAudio';
import { CharacterPortrait } from '../components/CharacterPortrait/CharacterPortrait';
import { GameShell } from '../components/layout/GameShell';
import { getBiomeTheme } from '../utils/biomeTheme';
import { AudioBus } from '../utils/AudioBus';

export function QuestionScreen() {
  const { state, dispatch } = useQuiz();
  const { playHover, playClick, playSuccess, playError, playBlocked } = useGameAudio();

  const pergunta = state.perguntasFase[state.indicePerguntaAtual] || state.perguntasFase[0];
  const [opcoesEliminadas, setOpcoesEliminadas] = useState<number[]>([]);
  const [dicaConselho, setDicaConselho] = useState<number | null>(null);
  const [showUbuntuStats, setShowUbuntuStats] = useState(false);
  const timeoutFiredRef = useRef(false);

  // Game feel: fase de "reveal" entre o clique e a troca de tela. O botão
  // reage (pop/shake), a tela pisca, o combo aparece e o +Axé voa ao HUD;
  // só então despacha a ação (mecânica intocada, apenas adiada ~0,6s).
  const [reveal, setReveal] = useState<{ chosenId: number; correct: boolean } | null>(null);
  const [flash, setFlash] = useState<'good' | 'bad' | null>(null);
  const [comboShow, setComboShow] = useState<number | null>(null);
  const [axeFly, setAxeFly] = useState(false);

  const ajudasMax = getAjudasMaxPorFase(state.faseAtual);
  const ajudasRestantes = ajudasMax - state.ajudasUsadasNestaFase;
  const podeUsarAjuda = ajudasRestantes > 0;

  const opcoesEmbaralhadas = useMemo(() => {
    if (!pergunta || !pergunta.opcoes) return [];
    const mapeadas = pergunta.opcoes.map((texto: string, i: number) => ({ texto, idOriginal: i }));
    return shuffleArray(mapeadas);
  }, [state.indicePerguntaAtual, pergunta?.id]);

  useEffect(() => {
    setOpcoesEliminadas([]);
    setDicaConselho(null);
    setShowUbuntuStats(false);
    timeoutFiredRef.current = false;
    setReveal(null);
    setFlash(null);
    setComboShow(null);
    setAxeFly(false);
  }, [state.indicePerguntaAtual, pergunta?.id]);

  useEffect(() => {
    if (state.status !== 'PLAYING' || !pergunta || state.tempoRestantePergunta <= 0) return;
    if (state.overlayFeedback?.visible || reveal) return; // durante o reveal o timer congela
    const id = setInterval(() => dispatch({ type: 'TICK_TIMER' }), 1000);
    return () => clearInterval(id);
  }, [state.status, state.tempoRestantePergunta, state.overlayFeedback?.visible, dispatch, pergunta, reveal]);

  const [loadingMinigame, setLoadingMinigame] = useState(false);

  useEffect(() => {
    if (state.tempoRestantePergunta !== 0 || !pergunta || state.status !== 'PLAYING') return;
    if (state.overlayFeedback?.visible || reveal) return;
    if (timeoutFiredRef.current) return;
    timeoutFiredRef.current = true;
    playError();
    setFlash('bad'); // esgotou o tempo = perda: pisca vermelho antes do portal
    dispatch({ type: 'ANSWER_INCORRECT', payload: pergunta });
  }, [state.tempoRestantePergunta, pergunta, state.status, dispatch, playError, reveal]);

  // Executa a mecânica de fato (após a animação de reveal). Nada aqui mudou em
  // relação à lógica original — só foi extraído para rodar adiado.
  const commitAnswer = async (correct: boolean) => {
    if (correct) {
      const isMinigameTrigger = state.indicePerguntaAtual === 3 || state.indicePerguntaAtual === 11;
      if (isMinigameTrigger) {
        setLoadingMinigame(true);
        const { fetchDesafioAleatorio } = await import('../services/quizFirebaseService');
        const desafioSorteado = await fetchDesafioAleatorio(state.faseAtual, state.desafiosConcluidosNestaJornada);
        setLoadingMinigame(false);
        dispatch({ type: 'ANSWER_CORRECT', payload: { velocidadeBonus: 5, desafioSorteado } });
      } else {
        dispatch({ type: 'ANSWER_CORRECT', payload: { velocidadeBonus: 5 } });
      }
    } else {
      dispatch({ type: 'ANSWER_INCORRECT', payload: pergunta });
    }
  };

  const handleResponder = (index: number) => {
    if (opcoesEliminadas.includes(index) || reveal) return; // trava cliques durante o reveal

    const correct = index === pergunta.correta_index;
    setReveal({ chosenId: index, correct });

    if (correct) {
      playSuccess();
      setFlash('good');
      setAxeFly(true);
      const combo = ((state as { comboAtual?: number }).comboAtual ?? 0) + 1;
      if (combo >= 3) setComboShow(combo);
    } else {
      playError(); // feedback.wrong no shake
      setFlash('bad');
      window.setTimeout(() => AudioBus.play('feedback.reveal'), 150); // resposta certa acende
    }

    // Despacha depois da animação (acerto ~0,62s p/ o pop+voo, erro ~0,56s p/ o hit).
    window.setTimeout(() => { void commitAnswer(correct); }, correct ? 620 : 560);
  };

  const mostrarMensagemAjudaIndisponivel = () => {
    playBlocked(); // cota esgotada = ação bloqueada (ui.blocked), não erro de resposta
    if (ajudasMax === 0) {
      dispatch({
        type: 'SHOW_FEEDBACK',
        payload: { message: 'Na Fase 50 (O Ápice), poderes de ajuda não são permitidos.', type: 'info' },
      });
    } else {
      dispatch({
        type: 'SHOW_FEEDBACK',
        payload: { message: `Cota de ajudas esgotada (${state.ajudasUsadasNestaFase}/${ajudasMax} usadas nesta fase).`, type: 'warning' },
      });
    }
  };

  const handleAjudaComAudio = (acao: () => void) => {
    if (!podeUsarAjuda) {
      mostrarMensagemAjudaIndisponivel();
      return;
    }
    playSuccess();
    acao();
  };

  const usarAxe = () => {
    if (state.inventario.axe_dos_deuses <= 0) {
      playBlocked();
      dispatch({ type: 'SHOW_FEEDBACK', payload: { message: 'Você não possui o item Axé dos Deuses no inventário. Adquira na Tenda!', type: 'warning' } });
      return;
    }
    handleAjudaComAudio(() => {
      const erradas = pergunta.opcoes
        .map((_: string, i: number) => i)
        .filter((i: number) => i !== pergunta.correta_index);
      const eliminar = erradas.slice(0, 2);
      setOpcoesEliminadas(eliminar);
      dispatch({ type: 'USAR_AJUDA', payload: { tipo: 'axe_dos_deuses' } });
    });
  };

  const usarUbuntu = () => {
    if (state.inventario.ubuntu <= 0) {
      playBlocked();
      dispatch({ type: 'SHOW_FEEDBACK', payload: { message: 'Você não possui o item Ubuntu no inventário. Adquira na Tenda!', type: 'warning' } });
      return;
    }
    handleAjudaComAudio(() => {
      setShowUbuntuStats(true);
      dispatch({ type: 'USAR_AJUDA', payload: { tipo: 'ubuntu' } });
    });
  };

  const usarConselho = () => {
    if (state.inventario.conselho_grios <= 0) {
      playBlocked();
      dispatch({ type: 'SHOW_FEEDBACK', payload: { message: 'Você não possui o item Conselho dos Griôs no inventário. Adquira na Tenda!', type: 'warning' } });
      return;
    }
    handleAjudaComAudio(() => {
      const certo = Math.random() < 0.8 ? pergunta.correta_index : pergunta.opcoes.map((_: string, i: number) => i).filter((i: number) => i !== pergunta.correta_index)[0] ?? 0;
      setDicaConselho(certo);
      dispatch({ type: 'USAR_AJUDA', payload: { tipo: 'conselho_grios' } });
    });
  };

  const usarCaminho = async () => {
    if (state.inventario.caminho_mandela <= 0) {
      playBlocked();
      dispatch({ type: 'SHOW_FEEDBACK', payload: { message: 'Você não possui o item Caminho de Mandela no inventário. Adquira na Tenda!', type: 'warning' } });
      return;
    }
    handleAjudaComAudio(async () => {
      const nextIdx = state.indicePerguntaAtual + 1;
      const isMinigameTrigger = nextIdx === 3 || nextIdx === 11;

      if (isMinigameTrigger) {
        setLoadingMinigame(true);
        const { fetchDesafioAleatorio } = await import('../services/quizFirebaseService');
        const desafioSorteado = await fetchDesafioAleatorio(state.faseAtual, state.desafiosConcluidosNestaJornada);
        setLoadingMinigame(false);
        dispatch({ type: 'PULAR_PERGUNTA', payload: { desafioSorteado } });
      } else {
        dispatch({ type: 'PULAR_PERGUNTA' });
      }
    });
  };

  if (!pergunta) {
    return (
      <div className="flex-1 h-full flex flex-col items-center justify-center p-6 text-center text-slate-400 font-cyber tracking-widest animate-pulse">
        Restaurando perguntas da jornada...
      </div>
    );
  }

  const opcoesVisiveis = opcoesEmbaralhadas.filter(({ idOriginal }: { idOriginal: number }) => !opcoesEliminadas.includes(idOriginal));

  // GD4: Estilo visual para botões de ajuda diferenciados por cota vs inventário
  const getHelpButtonStyle = (qty: number, colorHover: string) => {
    if (ajudasRestantes <= 0) {
      return `p-1.5 clip-hex glass-panel opacity-25 grayscale cursor-not-allowed border-slate-700`;
    }
    if (qty <= 0) {
      return `p-1.5 clip-hex glass-panel border-amber-500/30 bg-amber-950/20 text-amber-500 hover:border-amber-400 cursor-pointer`;
    }
    return `p-1.5 clip-hex glass-panel hover:border-${colorHover} hover:shadow-neon-${colorHover.split('-')[0]} cursor-pointer group`;
  };

  return (
    <GameShell maxWidth="xl" maxHeightCeiling="max-h-[100dvh]" frameRgb={getBiomeTheme(state.faseAtual).rgb} className="justify-between">

      {/* Top Anchor: Barra Meta (Progresso, Cronômetro, Poderes) colada abaixo do HUD */}
      <div className="glass-panel p-1.5 sm:p-2 px-2.5 sm:px-3 rounded-xl border-slate-700/50 flex justify-between items-center gap-2 shrink-0 w-full mt-0.5">

        {/* Progresso & Duelo */}
        <div className="flex items-center gap-1.5">
          <div className="bg-slate-900 border border-slate-700 text-sky-400 px-2 py-0.5 sm:py-1 rounded-full text-fluid-sm font-cyber font-bold tracking-wider">
            Q. {state.indicePerguntaAtual + 1} / 16
          </div>
          {state.dueloAtivo && (
            <div className="bg-amber-900/40 text-amber-500 border border-amber-500/50 px-2 py-0.5 rounded-full text-[9px] font-cyber font-bold tracking-wider animate-pulse">
              🔥 Tudo ou Nada
            </div>
          )}
        </div>

        {/* Status (Timer + Poderes) */}
        <div className="flex items-center gap-1.5 sm:gap-2">
          {state.tempoRestantePergunta > 0 && (
            <div className={`flex items-center gap-1 font-cyber font-bold text-fluid-body px-1
              ${state.tempoRestantePergunta <= 5 ? 'text-red-500 animate-pulse drop-shadow-[0_0_8px_rgba(239,68,68,0.8)]' : 'text-amber-400 drop-shadow-[0_0_5px_rgba(245,158,11,0.5)]'}`}
            >
              <Clock size={14} /> {state.tempoRestantePergunta}s
            </div>
          )}

          <div className="flex gap-1">
            <button
              onMouseEnter={playHover}
              onClick={usarAxe}
              className={getHelpButtonStyle(state.inventario.axe_dos_deuses, 'amber-400')}
              title={ajudasRestantes <= 0 ? 'Cota de ajudas esgotada nesta fase' : state.inventario.axe_dos_deuses <= 0 ? 'Sem inventário (Adquira na Tenda)' : 'Axé dos Deuses (elimina 2 opções)'}
            >
              <Zap size={14} className={state.inventario.axe_dos_deuses <= 0 && ajudasRestantes > 0 ? 'text-amber-500/60' : 'text-amber-400'} />
            </button>
            <button
              onMouseEnter={playHover}
              onClick={usarUbuntu}
              className={getHelpButtonStyle(state.inventario.ubuntu, 'sky-400')}
              title={ajudasRestantes <= 0 ? 'Cota de ajudas esgotada nesta fase' : state.inventario.ubuntu <= 0 ? 'Sem inventário (Adquira na Tenda)' : 'Ubuntu (estatísticas)'}
            >
              <Target size={14} className={state.inventario.ubuntu <= 0 && ajudasRestantes > 0 ? 'text-sky-500/60' : 'text-sky-400'} />
            </button>
            <button
              onMouseEnter={playHover}
              onClick={usarConselho}
              className={getHelpButtonStyle(state.inventario.conselho_grios, 'purple-400')}
              title={ajudasRestantes <= 0 ? 'Cota de ajudas esgotada nesta fase' : state.inventario.conselho_grios <= 0 ? 'Sem inventário (Adquira na Tenda)' : 'Conselho dos Griôs (dica 80%)'}
            >
              <HelpCircle size={14} className={state.inventario.conselho_grios <= 0 && ajudasRestantes > 0 ? 'text-purple-500/60' : 'text-purple-400'} />
            </button>
            <button
              onMouseEnter={playHover}
              onClick={usarCaminho}
              className={getHelpButtonStyle(state.inventario.caminho_mandela, 'emerald-400')}
              title={ajudasRestantes <= 0 ? 'Cota de ajudas esgotada nesta fase' : state.inventario.caminho_mandela <= 0 ? 'Sem inventário (Adquira na Tenda)' : 'Caminho de Mandela (pular pergunta)'}
            >
              <FastForward size={14} className={state.inventario.caminho_mandela <= 0 && ajudasRestantes > 0 ? 'text-emerald-500/60' : 'text-emerald-400'} />
            </button>
          </div>
        </div>
      </div>

      {/* Módulo coeso: pergunta + opções tratados como UMA unidade e centralizados
          no espaço restante. A folga da tela vira margem simétrica externa (via
          justify-center), nunca um vão ENTRE a pergunta e as respostas. O respiro
          interno entre os dois blocos é contido por clamp (12px→28px), jamais 197px.
          Ocupação (9.2): a caixa da pergunta e os botões crescem por dvh para o
          módulo ocupar ≥60% do palco em telas altas — o ganho vai para o CONTEÚDO,
          não para o vão. Piso em 360×640 intacto (clamps caem no mínimo). */}
      <div className="flex-1 min-h-0 w-full flex flex-col justify-center">
        <div className={`gameplay-split w-full flex flex-col gap-[clamp(0.75rem,3dvh,1.75rem)] ${reveal && !reveal.correct ? 'juice-stage-hit' : ''}`}>

          {/* Bloco Pergunta (enunciado + retrato + Ubuntu) */}
          <div className="flex flex-col gap-2">
            <div className="split-qbox glass-panel p-3 sm:p-4 rounded-xl border-l-4 border-l-sky-500 flex items-center gap-3 sm:gap-4 shadow-lg min-h-[clamp(5.5rem,15dvh,10rem)]">
              <CharacterPortrait fase={state.faseAtual} variant="inline" showLabel={false} eager />
              <h2 className="text-fluid-title font-bold text-slate-100 leading-snug drop-shadow-md flex-1">
                {pergunta.pergunta}
              </h2>
            </div>

            {/* Dica Ubuntu se ativa */}
            {showUbuntuStats && (
              <div className="p-2 glass-panel border-sky-500/30 text-fluid-sm text-sky-200 flex items-center gap-2 animate-in slide-in-from-top-2">
                <Target size={14} className="text-sky-400 animate-pulse shrink-0" />
                <span className="font-cyber tracking-wider uppercase">Ubuntu: <strong className="text-white">A 45%, B 30%, C 15%, D 10%</strong></span>
              </div>
            )}
          </div>

          {/* Bloco Ação: Opções de Resposta + Savepoint */}
          <div className="w-full flex flex-col gap-2">
            <div className="grid gap-2 relative">
              {loadingMinigame && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-cyber-900/80 backdrop-blur-md rounded-xl border border-amber-500/50 shadow-neon-amber">
                  <div className="w-8 h-8 rounded-full border-3 border-amber-500 border-t-transparent animate-spin"></div>
                </div>
              )}

              {opcoesVisiveis.map(({ texto, idOriginal }: { texto: string; idOriginal: number }, idx: number) => {
                const isDica = dicaConselho === idOriginal;
                const letter = String.fromCharCode(65 + idx);

                // Juice: reação do botão no reveal (escolhido acerta/erra; a
                // resposta certa é revelada quando o jogador erra).
                const isChosen = reveal?.chosenId === idOriginal;
                const isTheCorrect = idOriginal === pergunta.correta_index;
                let juiceClass = '';
                if (reveal) {
                  if (isChosen) juiceClass = reveal.correct ? 'juice-correct' : 'juice-wrong';
                  else if (!reveal.correct && isTheCorrect) juiceClass = 'juice-reveal';
                }
                const atenuado = reveal && !isChosen && !(juiceClass === 'juice-reveal');

                return (
                  <button
                    key={idOriginal}
                    onMouseEnter={playHover}
                    onClick={() => { playClick(); handleResponder(idOriginal); }}
                    disabled={loadingMinigame || !!reveal}
                    className={`btn-cyber w-full text-left px-3 py-2.5 sm:py-3 rounded-xl border flex items-center gap-2.5 transition-all group min-h-[clamp(3rem,7.5dvh,4.5rem)] ${juiceClass}
                      ${isDica
                        ? 'border-amber-400 bg-amber-900/30 text-amber-100 shadow-neon-amber'
                        : 'border-slate-700/80 bg-slate-900/70 text-slate-200 hover:text-white hover:border-sky-400 hover:shadow-neon-cyan hover:bg-slate-800'
                      } ${loadingMinigame ? 'opacity-50 cursor-not-allowed' : ''} ${atenuado ? 'opacity-40' : ''}`}
                  >
                    <div className={`shrink-0 w-6 h-6 sm:w-7 sm:h-7 rounded bg-slate-950 border flex items-center justify-center font-cyber font-bold text-fluid-body
                      ${isDica ? 'border-amber-400 text-amber-400' : 'border-slate-600 text-slate-400 group-hover:border-sky-400 group-hover:text-sky-400'}`}>
                      {letter}
                    </div>
                    <span className="font-medium text-fluid-body flex-1 leading-snug">{texto}</span>

                    {isDica && (
                      <span className="bg-amber-500 text-slate-950 font-cyber font-bold text-fluid-xs uppercase px-2 py-0.5 rounded-full animate-pulse">
                        Dica Griô
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* Savepoint Checkpoint Footer */}
            {state.checkpointAtual > 0 && (
              <div className="flex items-center justify-center pt-0.5">
                <div className="px-3 py-0.5 rounded-full border border-emerald-500/30 bg-emerald-900/20 text-emerald-400 text-fluid-xs font-cyber font-bold tracking-wider uppercase flex items-center gap-1.5">
                  <Shield size={11} /> Save point (pergunta {state.checkpointAtual + 1})
                </div>
              </div>
            )}
          </div>

        </div>
      </div>

      {/* ── Overlays de game feel (fixed, pointer-events-none) ─────────────── */}

      {/* Flash de tela: vinheta que pisca na cor do evento (verde=ganho, vermelho=perda) */}
      {flash && (
        <div
          className={`juice-flash ${flash === 'good' ? 'juice-flash-good' : 'juice-flash-bad'}`}
          onAnimationEnd={() => setFlash(null)}
        />
      )}

      {/* +Axé voando do palco rumo ao HUD (canto superior esquerdo) — sensação de ganho */}
      {axeFly && (
        <div className="fixed inset-0 z-40 pointer-events-none flex items-center justify-center">
          <div
            className="juice-axe-fly flex items-center gap-1 font-cyber font-black text-emerald-300 text-fluid-subtitle drop-shadow-[0_0_12px_rgba(16,185,129,0.9)]"
            style={{ ['--axe-dx' as string]: '-38vw', ['--axe-dy' as string]: '-40vh' }}
            onAnimationEnd={() => setAxeFly(false)}
          >
            <Coins size={18} className="shrink-0" /> +Axé
          </div>
        </div>
      )}

      {/* COMBO ×N — reforço de sequência de acertos */}
      {comboShow && (
        <div className="fixed inset-x-0 top-[22%] z-40 pointer-events-none flex justify-center">
          <div className="juice-axe-fly flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/90 text-slate-950 font-cyber font-black uppercase tracking-wider text-fluid-body shadow-[0_0_18px_rgba(245,158,11,0.7)]">
            <Flame size={16} className="shrink-0" /> Combo ×{comboShow}
          </div>
        </div>
      )}

    </GameShell>
  );
}
